"use client"

import * as React from "react"
import Image from "next/image"
import { useTheme } from "next-themes"
import { 
    Gemini, 
    DeepSeek,
    Grok,
    Claude,
    OpenAI,
    Meta as MetaIcon,
    Perplexity as PerplexityIcon,
    Stability,
    Mistral,
    Replicate,
    Ollama as OllamaIcon,
    OpenRouter as OpenRouterIcon,
    Fal,
    Alibaba,
    OpenChat,
    Flux, 
    Qwen,
} from "@lobehub/icons"

import { cn } from "@/lib/utils"
import { P } from "./p"


function ProviderIcon({
    className="h-3.5 w-3.5",
    ...props
}: Omit<React.ComponentProps<typeof Image>, "src" | "alt"> & {
    maker: string,
    fallbackIcon?: string | null
}) {
    // console.log('props', props)
    const icons: { [id: string]: string } = {
        cohere: "/icons/cohere.svg",
        openai: "/icons/openai.svg",
        huggingface: "/icons/huggingface.svg",
        anthropic: "/icons/anthropic.svg",
        replicate: "/icons/meta.svg",
        openchat:
            "https://mybucket-blendai.s3.amazonaws.com/e3fdfc29-0e4d-40c7-99db-84a9b7a0d197/1bbf855f-db41-4d15-9b9e-a830f2ed1110.png",
    }
    const { theme, setTheme } = useTheme()

    const iconPath = icons[props.maker]

    const mapping: { [id: string]: any } = {
        'replicate': <Replicate className={className} />,
        'mistralai': <Mistral.Color className={className} />,
        'openai': <OpenAI className={className} />,
        'meta': <MetaIcon.Color className={className} />,
        'perplexity': <PerplexityIcon.Color className={className} />,
        'blendai': <BlendAINewLogo className={className} />,
        'stabilityai': <Stability.Color className={className} />,
        'anthropic': <Claude.Color className={className} />,
        'google': <Gemini.Color className={className} />,
        'ollama': <OllamaIcon className={className} />,
        'openchat': <OpenChat.Color className={className} />,
        'openrouter': <OpenRouterIcon className={className} />,
        'falai': <Fal.Color className={className} />,
        'black forest labs': <Flux className={className} />,
        'deepseek': <DeepSeek.Color className={className} />,
        'xai': <Grok className={className} />,
        'alibaba': <Alibaba.Color className={className} />,
        'qwen': <Qwen.Color className={className} />,
    }

    // console.log('props.maker', props.maker.toLowerCase())


    return (
        <div className="flex items-center justify-center">
            {mapping[props.maker.toLowerCase()] ? (
                mapping[props.maker.toLowerCase()]
            ) : ["openchat", "gryphe"].includes(props.maker) ? (
                <OpenChat.Color className={className} />
            ) : ["falai", "black forest labs"].includes(props.maker) ? (
                <Fal.Color className={className} />
            ) : props.fallbackIcon ? (
                mapping[props.fallbackIcon.toLowerCase()]
            ) : (
                <p>?</p>
            )}
        </div>
    )
}





















































































































function GithubLogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 256 256"
            className={cn("h-4 w-4 fill-foreground", className)}
            {...props}
        >
            <path d="M208.31,75.68A59.78,59.78,0,0,0,202.93,28,8,8,0,0,0,196,24a59.75,59.75,0,0,0-48,24H124A59.75,59.75,0,0,0,76,24a8,8,0,0,0-6.93,4,59.78,59.78,0,0,0-5.38,47.68A58.14,58.14,0,0,0,56,104v8a56.06,56.06,0,0,0,48.44,55.47A39.8,39.8,0,0,0,96,192v8H72a24,24,0,0,1-24-24A40,40,0,0,0,8,136a8,8,0,0,0,0,16,24,24,0,0,1,24,24,40,40,0,0,0,40,40H96v16a8,8,0,0,0,16,0V192a24,24,0,0,1,48,0v40a8,8,0,0,0,16,0V192a39.8,39.8,0,0,0-8.44-24.53A56.06,56.06,0,0,0,216,112v-8A58.14,58.14,0,0,0,208.31,75.68ZM200,112a40,40,0,0,1-40,40H112a40,40,0,0,1-40-40v-8a41.74,41.74,0,0,1,6.9-22.48A8,8,0,0,0,80,73.83a43.81,43.81,0,0,1,.79-33.58,43.88,43.88,0,0,1,32.32,20.06A8,8,0,0,0,119.82,64h32.35a8,8,0,0,0,6.74-3.69,43.87,43.87,0,0,1,32.32-20.06A43.81,43.81,0,0,1,192,73.83a8.09,8.09,0,0,0,1,7.65A41.72,41.72,0,0,1,200,104Z" />
        </svg>
    )
}

function OllamaLogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            width="646"
            height="854"
            viewBox="0 0 646 854"
            className={cn("h-4 w-4 rounded-sm bg-white pt-[1px] ", className)}
        >
            <path d="M140.629 0.239929C132.66 1.52725 123.097 5.69568 116.354 10.845C95.941 26.3541 80.1253 59.2728 73.4435 100.283C70.9302 115.792 69.2138 137.309 69.2138 153.738C69.2138 173.109 71.4819 197.874 74.7309 214.977C75.4665 218.778 75.8343 222.15 75.5278 222.395C75.2826 222.64 72.2788 225.092 68.9072 227.789C57.3827 236.984 44.2029 251.145 35.1304 264.08C17.7209 288.784 6.44151 316.86 1.72133 347.265C-0.117698 359.28 -0.608106 383.555 0.863118 395.57C4.11207 423.278 12.449 446.695 26.7321 468.151L31.391 475.078L30.0424 477.346C20.4794 493.407 12.3264 516.64 8.52575 538.953C5.522 556.608 5.15419 561.328 5.15419 584.99C5.15419 608.837 5.4607 613.557 8.28054 630.047C11.6521 649.786 18.5178 670.689 26.1804 684.605C28.6938 689.141 34.8239 698.581 35.5595 699.072C35.8047 699.194 35.0691 701.462 33.9044 704.098C25.077 723.408 17.537 749.093 14.4106 770.733C12.2038 785.567 11.8973 790.349 11.8973 805.981C11.8973 825.903 13.0007 835.589 17.1692 851.466L17.7822 853.795H44.019H70.3172L68.6007 850.546C57.9957 830.93 57.0149 794.517 66.1487 758.166C70.3172 741.369 75.0374 729.048 83.8647 712.067L89.1366 701.769V695.455C89.1366 689.57 89.014 688.896 87.1137 685.034C85.6424 682.091 83.6808 679.578 80.1866 676.145C74.2404 670.383 69.9494 664.314 66.5165 656.835C51.4365 624.1 48.494 575.489 59.0991 534.049C63.5128 516.762 70.8076 501.376 78.4702 492.978C83.6808 487.215 86.378 480.779 86.378 474.097C86.378 467.17 83.926 461.469 78.4089 455.523C62.5932 438.604 52.8464 418.006 49.3522 394.038C44.3868 359.893 53.3981 322.683 73.8726 293.198C93.9181 264.263 122.055 245.689 153.503 240.724C160.552 239.559 173.732 239.743 181.088 241.092C189.119 242.502 194.145 242.072 199.295 239.62C205.67 236.617 208.858 232.877 212.597 224.295C215.907 216.633 218.482 212.464 225.409 203.821C233.746 193.461 241.776 186.411 254.649 177.89C269.362 168.266 286.097 161.278 302.771 157.906C308.839 156.68 311.659 156.496 323 156.496C334.341 156.496 337.161 156.68 343.229 157.906C367.688 162.872 391.964 175.5 411.335 193.399C415.503 197.261 425.495 209.644 428.683 214.794C429.909 216.816 432.055 221.108 433.403 224.295C437.142 232.877 440.33 236.617 446.705 239.62C451.671 242.011 456.881 242.502 464.605 241.214C476.804 239.13 486.183 239.314 498.137 241.766C538.841 249.98 574.273 283.512 589.966 328.446C603.636 367.862 599.774 409.118 579.422 440.626C575.989 445.96 572.556 450.251 567.591 455.523C556.863 466.986 556.863 481.208 567.53 492.978C585.062 512.165 596.035 559.367 592.724 600.99C590.518 628.453 583.468 653.035 573.782 666.95C572.066 669.402 568.511 673.57 565.813 676.145C562.319 679.578 560.358 682.091 558.886 685.034C556.986 688.896 556.863 689.57 556.863 695.455V701.769L562.135 712.067C570.963 729.048 575.683 741.369 579.851 758.166C588.863 794.027 588.066 829.704 577.767 849.995C576.909 851.711 576.173 853.305 576.173 853.489C576.173 853.673 587.882 853.795 602.226 853.795H628.218L628.892 851.159C629.26 849.75 629.873 847.604 630.179 846.378C630.854 843.681 632.202 835.712 633.306 828.049C634.348 820.325 634.348 791.881 633.306 783.299C629.383 752.158 622.823 727.454 612.096 704.098C610.931 701.462 610.195 699.194 610.44 699.072C610.747 698.888 612.463 696.436 614.302 693.677C627.666 673.448 635.88 648.008 640.049 614.415C641.152 605.158 641.152 565.374 640.049 556.485C637.106 533.559 633.551 517.988 627.666 502.234C625.214 495.675 618.716 481.821 615.958 477.346L614.609 475.078L619.268 468.151C633.551 446.695 641.888 423.278 645.137 395.57C646.608 383.555 646.118 359.28 644.279 347.265C639.497 316.798 628.279 288.845 610.87 264.08C601.797 251.145 588.617 236.984 577.093 227.789C573.721 225.092 570.717 222.64 570.472 222.395C570.166 222.15 570.534 218.778 571.269 214.977C578.687 176.296 578.441 128.053 570.656 90.3524C563.913 57.4951 551.653 31.3808 535.837 16.3008C523.209 4.28578 510.336 -0.863507 494.888 0.11731C459.456 2.20154 430.89 42.9667 419.61 107.21C417.771 117.57 416.178 129.708 416.178 133.018C416.178 134.305 415.932 135.347 415.626 135.347C415.319 135.347 412.929 134.121 410.354 132.589C383.014 116.405 352.608 107.762 323 107.762C293.392 107.762 262.986 116.405 235.646 132.589C233.071 134.121 230.681 135.347 230.374 135.347C230.068 135.347 229.822 134.305 229.822 133.018C229.822 129.585 228.167 117.08 226.39 107.21C216.152 49.5259 192.674 11.3354 161.472 1.71112C157.181 0.423799 144.982 -0.434382 140.629 0.239929ZM151.051 50.139C159.878 57.1273 169.686 77.1114 175.326 99.4863C176.368 103.532 177.471 108.191 177.778 109.907C178.023 111.563 178.697 115.302 179.249 118.183C181.64 131.179 182.743 145.217 182.866 162.32L182.927 179.178L178.697 185.43L174.468 191.744H164.598C153.074 191.744 141.61 193.216 130.637 196.158C126.714 197.139 122.913 198.12 122.178 198.304C121.013 198.549 120.829 198.181 120.155 193.154C116.538 165.875 116.722 135.654 120.707 110.52C125.12 82.5059 135.419 57.1273 145.472 49.6486C147.863 47.8708 148.292 47.9321 151.051 50.139ZM500.589 49.7098C506.658 54.1848 513.34 66.0772 518.305 81.2798C528.297 111.685 531.117 153.431 525.845 193.154C525.171 198.181 524.987 198.549 523.822 198.304C523.087 198.12 519.286 197.139 515.363 196.158C504.39 193.216 492.926 191.744 481.402 191.744H471.532L467.303 185.43L463.073 179.178L463.134 162.32C463.257 138.535 465.464 119.961 470.735 99.3024C476.314 77.1114 486.183 57.1273 494.949 50.139C497.708 47.9321 498.137 47.8708 500.589 49.7098Z" />
            <path d="M313.498 358.237C300.195 359.525 296.579 360.015 290.203 361.303C279.843 363.448 265.989 368.23 256.365 372.95C222.895 389.317 199.846 416.596 192.796 448.166C191.386 454.419 191.202 456.503 191.202 467.047C191.202 477.468 191.386 479.736 192.735 485.682C202.114 526.938 240.12 557.405 289.284 562.983C299.95 564.148 346.049 564.148 356.715 562.983C396.193 558.508 430.154 537.114 445.418 507.076C449.463 499.046 451.425 493.835 453.264 485.682C454.613 479.736 454.797 477.468 454.797 467.047C454.797 456.503 454.613 454.419 453.203 448.166C442.965 402.313 398.461 366.207 343.903 359.341C336.792 358.483 318.157 357.747 313.498 358.237ZM336.424 391.585C354.631 393.547 372.96 400.045 387.672 409.853C395.58 415.125 406.737 426.159 411.518 433.393C417.403 442.342 420.774 451.476 422.307 462.572C422.981 467.66 422.614 471.522 420.774 479.736C417.893 491.996 408.943 504.808 396.867 513.758C391.227 517.865 379.519 523.812 372.347 526.141C358.738 530.493 349.849 531.29 318.095 531.045C297.376 530.861 293.697 530.677 287.751 529.574C267.461 525.773 251.4 517.681 239.753 505.36C230.312 495.429 226.021 486.357 223.692 471.706C222.65 464.901 224.611 453.622 228.596 444.12C233.439 432.534 245.944 418.129 258.327 409.853C272.671 400.29 291.552 393.486 308.9 391.647C315.582 390.911 329.742 390.911 336.424 391.585Z" />
            <path d="M299.584 436.336C294.925 438.849 291.676 445.224 292.657 449.944C293.76 455.032 298.235 460.182 305.223 464.412C308.963 466.68 309.208 466.986 309.392 469.254C309.514 470.603 309.024 474.465 308.35 477.898C307.614 481.269 307.062 484.825 307.062 485.806C307.124 488.442 309.576 492.733 312.15 494.817C314.419 496.656 314.848 496.717 321.223 496.901C327.047 497.085 328.273 496.962 330.602 495.859C336.61 492.916 338.142 487.522 335.935 477.162C334.096 468.519 334.464 467.17 339.062 464.534C343.904 461.714 349.054 456.749 350.586 453.377C353.529 446.941 350.831 439.646 344.333 436.274C342.74 435.477 340.778 435.11 337.897 435.11C333.422 435.11 330.541 436.152 325.269 439.523L322.265 441.424L320.365 440.259C312.58 435.661 311.17 435.11 306.449 435.171C303.078 435.171 301.239 435.477 299.584 436.336Z" />
            <path d="M150.744 365.165C139.894 368.598 131.802 376.567 127.634 387.908C125.611 393.303 124.63 401.824 125.488 406.421C127.511 417.394 136.522 427.386 146.76 430.145C159.633 433.516 169.257 431.309 177.778 422.85C182.743 418.007 185.441 413.777 188.138 406.911C190.099 402.069 190.222 401.211 190.222 394.345L190.283 386.989L187.709 381.717C183.601 373.38 176.184 367.188 167.602 364.92C162.759 363.694 154.974 363.756 150.744 365.165Z" />
            <path d="M478.153 364.982C469.755 367.25 462.276 373.502 458.291 381.717L455.717 386.989L455.778 394.345C455.778 401.211 455.901 402.069 457.862 406.911C460.56 413.777 463.257 418.007 468.222 422.85C476.743 431.309 486.367 433.516 499.241 430.145C506.658 428.183 514.075 421.93 517.631 414.635C520.696 408.444 521.431 403.969 520.451 396.919C518.183 380.797 508.742 369.089 494.704 364.982C490.597 363.756 482.628 363.756 478.153 364.982Z" />
        </svg>
    )
}

function GoogleLogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            className={cn("h-4 w-4 fill-foreground", className)}
        >
            <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
            />
            <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
            />
            <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
            />
            <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
            />
            <path d="M1 1h22v22H1z" fill="none" />
        </svg>
    )
}


function DeepSeekLogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
<svg  className={cn("h-5 w-5", 'fill-foreground')} height="1em"  viewBox="0 0 24 24" width="1em" xmlns="http://www.w3.org/2000/svg"><title>DeepSeek</title><path d="M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 01-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 00-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 01-.465.137 9.597 9.597 0 00-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.86.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 001.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 011.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.292.625.514.891.677-.802.09-2.14.11-3.054-.614zm1-6.44a.306.306 0 01.415-.287.302.302 0 01.2.288.306.306 0 01-.31.307.303.303 0 01-.304-.308zm3.11 1.596c-.2.081-.399.151-.59.16a1.245 1.245 0 01-.798-.254c-.274-.23-.47-.358-.552-.758a1.73 1.73 0 01.016-.588c.07-.327-.008-.537-.239-.727-.187-.156-.426-.199-.688-.199a.559.559 0 01-.254-.078c-.11-.054-.2-.19-.114-.358.028-.054.16-.186.192-.21.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.391.451.462.576.685.914.176.265.336.537.445.848.067.195-.019.354-.25.452z" fill="#4D6BFE"></path></svg>
    )
}

function XAI({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 841.89 595.28" className={cn("h-6 w-6", 'fill-foreground')} {...props}>
            <g>
                <polygon points="557.09,211.99 565.4,538.36 631.96,538.36 640.28,93.18  " />
                <polygon points="640.28,56.91 538.72,56.91 379.35,284.53 430.13,357.05  " />
                <polygon points="201.61,538.36 303.17,538.36 353.96,465.84 303.17,393.31  " />
                <polygon points="201.61,211.99 430.13,538.36 531.69,538.36 303.17,211.99  " />
            </g>
        </svg>
    )
}


function Moon({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn("h-4 w-4 fill-foreground", className)}
            {...props}
        >
            <path d="M233.54 142.23a8 8 0 0 0-8-2 88.08 88.08 0 0 1-109.8-109.8 8 8 0 0 0-10-10 104.84 104.84 0 0 0-52.91 37A104 104 0 0 0 136 224a103.09 103.09 0 0 0 62.52-20.88 104.84 104.84 0 0 0 37-52.91 8 8 0 0 0-1.98-7.98Zm-44.64 48.11A88 88 0 0 1 65.66 67.11a89 89 0 0 1 31.4-26A106 106 0 0 0 96 56a104.11 104.11 0 0 0 104 104 106 106 0 0 0 14.92-1.06 89 89 0 0 1-26.02 31.4Z" />
        </svg>
    )
}

function Sun({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn("h-4 w-4 fill-foreground", className)}
            {...props}
        >
            <path d="M120 40V16a8 8 0 0 1 16 0v24a8 8 0 0 1-16 0Zm72 88a64 64 0 1 1-64-64 64.07 64.07 0 0 1 64 64Zm-16 0a48 48 0 1 0-48 48 48.05 48.05 0 0 0 48-48ZM58.34 69.66a8 8 0 0 0 11.32-11.32l-16-16a8 8 0 0 0-11.32 11.32Zm0 116.68-16 16a8 8 0 0 0 11.32 11.32l16-16a8 8 0 0 0-11.32-11.32ZM192 72a8 8 0 0 0 5.66-2.34l16-16a8 8 0 0 0-11.32-11.32l-16 16A8 8 0 0 0 192 72Zm5.66 114.34a8 8 0 0 0-11.32 11.32l16 16a8 8 0 0 0 11.32-11.32ZM48 128a8 8 0 0 0-8-8H16a8 8 0 0 0 0 16h24a8 8 0 0 0 8-8Zm80 80a8 8 0 0 0-8 8v24a8 8 0 0 0 16 0v-24a8 8 0 0 0-8-8Zm112-88h-24a8 8 0 0 0 0 16h24a8 8 0 0 0 0-16Z" />
        </svg>
    )
}

function ArrowCircleRight({
    className,
    ...props
}: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
            className={cn("h-5 w-5", className)}
            {...props}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12.75 15l3-3m0 0l-3-3m3 3h-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
        </svg>
    )
}


export function AlibabaLogo({
    className,
    ...props
}: React.ComponentProps<"svg">) {
    return (
<svg className={cn("h-5 w-5 fill-primary", className)} version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 125 62.5">
 <g>
  <g>
   <path  d="M109.8,53.9c-2.3,0-4.2,1.9-4.2,4.3c0,2.4,1.9,4.3,4.2,4.3c2.3,0,4.2-1.9,4.2-4.3
			C114.1,55.9,112.2,53.9,109.8,53.9z M109.8,61.8c-2,0-3.6-1.6-3.6-3.6c0-2,1.6-3.6,3.6-3.6c2,0,3.6,1.6,3.6,3.6
			C113.4,60.2,111.8,61.8,109.8,61.8z">
   </path>
   <path  d="M110.8,58.6c-0.1-0.1-0.2-0.1-0.4-0.2c0.4-0.1,0.7-0.2,0.9-0.4c0.2-0.2,0.3-0.5,0.3-0.8
			c0-0.3-0.1-0.5-0.2-0.7c-0.1-0.2-0.3-0.3-0.5-0.4c-0.2-0.1-0.5-0.1-0.9-0.1h-2v4.5h0.6v-2h0.7c0.2,0,0.3,0,0.3,0
			c0.1,0,0.2,0.1,0.3,0.1c0.1,0.1,0.2,0.2,0.3,0.3c0.1,0.1,0.3,0.3,0.4,0.6l0.6,0.9h0.7l-0.8-1.2C111.2,58.9,111,58.7,110.8,58.6z
			 M110,57.9h-1.3v-1.5h1.4c0.3,0,0.6,0.1,0.7,0.2c0.1,0.1,0.2,0.3,0.2,0.5c0,0.1,0,0.3-0.1,0.4c-0.1,0.1-0.2,0.2-0.3,0.3
			C110.5,57.8,110.3,57.9,110,57.9z">
   </path>
  </g>
  <path  d="M73.4,46.8c6.9-9.2,19.9-22.1,20.3-31.2C94.5,3.8,82.6-0.1,70.3,0c-8.6,0.1-17.4,2.6-23.4,4.7
		C26,12.1,13,23.8,4.7,36.9C-4,49.8-1.1,62.1,17.3,62.5c14.1-0.6,23.3-4.5,32.9-9.4c0.1,0-26.5,7.6-36.2,2c0,0,0,0,0,0
		c-1-0.6-2.1-1.4-2.4-3.6c-0.1-4.6,7.6-9.4,11.9-10.9v-8c3.2,1.2,6.6,1.9,10.2,1.9c6.9,0,13.2-2.5,18.1-6.6c0.2,0.7,0.3,1.5,0.2,2.3
		h1.9c0.2-2.1-0.9-3.7-0.9-3.7c-1.7-2.8-4.8-2.7-4.8-2.7s1.6,0.7,2.8,2.4c-4.5,3.8-10.3,6-16.6,6c-2.7,0-5.3-0.4-7.7-1.2l6.3-6.3
		l-1.7-4.6c12.7-4.4,23.3-7.8,40.7-10.9L68,6.3l2-1.2C80.5,8,87.4,10.1,87,15.7c-0.2,0.9-0.5,2-1,3.2c-3,6-12.1,16.1-15.8,20.3
		c-2.4,2.8-4.8,5.5-6.5,8.1c-1.7,2.6-2.8,5.1-2.9,7.4C61,73,114.6,46.2,125,39.1C109.7,45.7,93.2,52,75,53.2
		C69.9,53.5,70.5,50.8,73.4,46.8">
  </path>
 </g>
</svg>    )
}

export function FalAILogo({
    className,
    ...props
}: React.ComponentProps<"svg">) {
    return (
        <svg className={cn("h-5 w-5", className)} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect opacity="0.22" x="7.38477" y="29.5391" width="2.46154" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.85" x="2.46094" y="19.6914" width="12.3077" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect x="4.92383" y="17.2305" width="9.84615" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.4" x="7.38477" y="27.0781" width="4.92308" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.7" y="22.1562" width="14.7692" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.5" x="7.38477" y="24.6133" width="7.38462" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.22" x="7.38477" y="12.3086" width="2.46154" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.85" x="2.46094" y="2.46094" width="12.3077" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect x="4.92383" width="9.84615" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.4" x="7.38477" y="9.84375" width="4.92308" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.7" y="4.92188" width="14.7692" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.5" x="7.38477" y="7.38281" width="7.38462" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.22" x="24.6152" y="29.5391" width="2.46154" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.85" x="19.6914" y="19.6914" width="12.3077" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect x="22.1543" y="17.2305" width="9.84615" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.4" x="24.6152" y="27.0781" width="4.92308" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.7" x="17.2305" y="22.1562" width="14.7692" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.5" x="24.6152" y="24.6133" width="7.38462" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.22" x="24.6152" y="12.3086" width="2.46154" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.85" x="19.6914" y="2.46094" width="12.3077" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect x="22.1543" width="9.84615" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.4" x="24.6152" y="9.84375" width="4.92308" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.7" x="17.2305" y="4.92188" width="14.7692" height="2.46154" fill="#5F4CD9">
            </rect>
            <rect opacity="0.5" x="24.6152" y="7.38281" width="7.38462" height="2.46154" fill="#5F4CD9">
            </rect>
        </svg>
    )
}






function IconUser({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn("h-4 w-4 fill-foreground", className)}
            {...props}
        >
            <path d="M230.92 212c-15.23-26.33-38.7-45.21-66.09-54.16a72 72 0 1 0-73.66 0c-27.39 8.94-50.86 27.82-66.09 54.16a8 8 0 1 0 13.85 8c18.84-32.56 52.14-52 89.07-52s70.23 19.44 89.07 52a8 8 0 1 0 13.85-8ZM72 96a56 56 0 1 1 56 56 56.06 56.06 0 0 1-56-56Z" />
        </svg>
    )
}

function IconResize({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 256 256"
            className={cn("h-4 w-4 fill-foreground", className)}
            {...props}
        >
            <path
                // fill="black"
                d="M216,48V96a8,8,0,0,1-16,0V67.31l-50.34,50.35a8,8,0,0,1-11.32-11.32L188.69,56H160a8,8,0,0,1,0-16h48A8,8,0,0,1,216,48ZM106.34,138.34,56,188.69V160a8,8,0,0,0-16,0v48a8,8,0,0,0,8,8H96a8,8,0,0,0,0-16H67.31l50.35-50.34a8,8,0,0,0-11.32-11.32Z"
            >

            </path>
        </svg>
    )
}

function IconGear({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            // width="16"
            // height="16"
            fill="currentColor"
            className={cn("h-4 w-4 fill-foreground", className)}
            {...props}
        >
            <path d="M128,80a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Zm88-29.84q.06-2.16,0-4.32l14.92-18.64a8,8,0,0,0,1.48-7.06,107.21,107.21,0,0,0-10.88-26.25,8,8,0,0,0-6-3.93l-23.72-2.64q-1.48-1.56-3-3L186,40.54a8,8,0,0,0-3.94-6,107.71,107.71,0,0,0-26.25-10.87,8,8,0,0,0-7.06,1.49L130.16,40Q128,40,125.84,40L107.2,25.11a8,8,0,0,0-7.06-1.48A107.6,107.6,0,0,0,73.89,34.51a8,8,0,0,0-3.93,6L67.32,64.27q-1.56,1.49-3,3L40.54,70a8,8,0,0,0-6,3.94,107.71,107.71,0,0,0-10.87,26.25,8,8,0,0,0,1.49,7.06L40,125.84Q40,128,40,130.16L25.11,148.8a8,8,0,0,0-1.48,7.06,107.21,107.21,0,0,0,10.88,26.25,8,8,0,0,0,6,3.93l23.72,2.64q1.49,1.56,3,3L70,215.46a8,8,0,0,0,3.94,6,107.71,107.71,0,0,0,26.25,10.87,8,8,0,0,0,7.06-1.49L125.84,216q2.16.06,4.32,0l18.64,14.92a8,8,0,0,0,7.06,1.48,107.21,107.21,0,0,0,26.25-10.88,8,8,0,0,0,3.93-6l2.64-23.72q1.56-1.48,3-3L215.46,186a8,8,0,0,0,6-3.94,107.71,107.71,0,0,0,10.87-26.25,8,8,0,0,0-1.49-7.06Zm-16.1-6.5a73.93,73.93,0,0,1,0,8.68,8,8,0,0,0,1.74,5.48l14.19,17.73a91.57,91.57,0,0,1-6.23,15L187,173.11a8,8,0,0,0-5.1,2.64,74.11,74.11,0,0,1-6.14,6.14,8,8,0,0,0-2.64,5.1l-2.51,22.58a91.32,91.32,0,0,1-15,6.23l-17.74-14.19a8,8,0,0,0-5-1.75h-.48a73.93,73.93,0,0,1-8.68,0,8,8,0,0,0-5.48,1.74L100.45,215.8a91.57,91.57,0,0,1-15-6.23L82.89,187a8,8,0,0,0-2.64-5.1,74.11,74.11,0,0,1-6.14-6.14,8,8,0,0,0-5.1-2.64L46.43,170.6a91.32,91.32,0,0,1-6.23-15l14.19-17.74a8,8,0,0,0,1.74-5.48,73.93,73.93,0,0,1,0-8.68,8,8,0,0,0-1.74-5.48L40.2,100.45a91.57,91.57,0,0,1,6.23-15L69,82.89a8,8,0,0,0,5.1-2.64,74.11,74.11,0,0,1,6.14-6.14A8,8,0,0,0,82.89,69L85.4,46.43a91.32,91.32,0,0,1,15-6.23l17.74,14.19a8,8,0,0,0,5.48,1.74,73.93,73.93,0,0,1,8.68,0,8,8,0,0,0,5.48-1.74L155.55,40.2a91.57,91.57,0,0,1,15,6.23L173.11,69a8,8,0,0,0,2.64,5.1,74.11,74.11,0,0,1,6.14,6.14,8,8,0,0,0,5.1,2.64l22.58,2.51a91.32,91.32,0,0,1,6.23,15l-14.19,17.74A8,8,0,0,0,199.87,123.66Z">

            </path>
        </svg>
    )
}

function PlusCircle({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            // fill="#000000"
            viewBox="0 0 256 256"
            className={cn("h-4 w-4 fill-foreground", className)}
        >
            <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm48-88a8,8,0,0,1-8,8H136v32a8,8,0,0,1-16,0V136H88a8,8,0,0,1,0-16h32V88a8,8,0,0,1,16,0v32h32A8,8,0,0,1,176,128Z">

            </path>
        </svg>
    )
}

function MinusCircle({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M176,128a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,128Zm56,0A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z">

            </path>
        </svg>
    )
}

function SignOut({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M112,216a8,8,0,0,1-8,8H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32h56a8,8,0,0,1,0,16H48V208h56A8,8,0,0,1,112,216Zm109.66-93.66-40-40a8,8,0,0,0-11.32,11.32L196.69,120H104a8,8,0,0,0,0,16h92.69l-26.35,26.34a8,8,0,0,0,11.32,11.32l40-40A8,8,0,0,0,221.66,122.34Z">

            </path>
        </svg>
    )
}

function User({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z">

            </path>
        </svg>
    )
}

function Pencil({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M227.31,73.37,182.63,28.68a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63ZM51.31,160,136,75.31,152.69,92,68,176.68ZM48,179.31,76.69,208H48Zm48,25.38L79.31,188,164,103.31,180.69,120Zm96-96L147.31,64l24-24L216,84.68Z">

            </path>
        </svg>
    )
}

function Check({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z">

            </path>
        </svg>
    )
}

function CaretDown({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
        </svg>
    )
}

function Circle({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z"></path>
        </svg>
    )
}

function ArrowLeft({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M224,128a8,8,0,0,1-8,8H59.31l58.35,58.34a8,8,0,0,1-11.32,11.32l-72-72a8,8,0,0,1,0-11.32l72-72a8,8,0,0,1,11.32,11.32L59.31,120H216A8,8,0,0,1,224,128Z"></path>
        </svg>
    )
}

function ArrowRight({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 rotate-180 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M224,128a8,8,0,0,1-8,8H59.31l58.35,58.34a8,8,0,0,1-11.32,11.32l-72-72a8,8,0,0,1,0-11.32l72-72a8,8,0,0,1,11.32,11.32L59.31,120H216A8,8,0,0,1,224,128Z"></path>
        </svg>
    )
}

function Microphone({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M128,176a48.05,48.05,0,0,0,48-48V64a48,48,0,0,0-96,0v64A48.05,48.05,0,0,0,128,176ZM96,64a32,32,0,0,1,64,0v64a32,32,0,0,1-64,0Zm40,143.6V232a8,8,0,0,1-16,0V207.6A80.11,80.11,0,0,1,48,128a8,8,0,0,1,16,0,64,64,0,0,0,128,0,8,8,0,0,1,16,0A80.11,80.11,0,0,1,136,207.6Z"></path>
        </svg>
    )
}

function Stop({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M200.73,40H55.27A15.29,15.29,0,0,0,40,55.27V200.73A15.29,15.29,0,0,0,55.27,216H200.73A15.29,15.29,0,0,0,216,200.73V55.27A15.29,15.29,0,0,0,200.73,40ZM200,200H56V56H200Z"></path>
        </svg>
    )
}

function Images({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M216,40H72A16,16,0,0,0,56,56V72H40A16,16,0,0,0,24,88V200a16,16,0,0,0,16,16H184a16,16,0,0,0,16-16V184h16a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM72,56H216v62.75l-10.07-10.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L72,109.37ZM184,200H40V88H56v80a16,16,0,0,0,16,16H184Zm32-32H72V132l36-36,49.66,49.66a8,8,0,0,0,11.31,0L194.63,120,216,141.38V168ZM160,84a12,12,0,1,1,12,12A12,12,0,0,1,160,84Z"></path>
        </svg>
    )
}

function ImagesSquare({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M208,32H80A16,16,0,0,0,64,48V64H48A16,16,0,0,0,32,80V208a16,16,0,0,0,16,16H176a16,16,0,0,0,16-16V192h16a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM80,48H208v69.38l-16.7-16.7a16,16,0,0,0-22.62,0L93.37,176H80Zm96,160H48V80H64v96a16,16,0,0,0,16,16h96Zm32-32H116l64-64,28,28v36Zm-88-64A24,24,0,1,0,96,88,24,24,0,0,0,120,112Zm0-32a8,8,0,1,1-8,8A8,8,0,0,1,120,80Z"></path>
        </svg>
    )
}

function IconSpinner({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M136,32V64a8,8,0,0,1-16,0V32a8,8,0,0,1,16,0Zm37.25,58.75a8,8,0,0,0,5.66-2.35l22.63-22.62a8,8,0,0,0-11.32-11.32L167.6,77.09a8,8,0,0,0,5.65,13.66ZM224,120H192a8,8,0,0,0,0,16h32a8,8,0,0,0,0-16Zm-45.09,47.6a8,8,0,0,0-11.31,11.31l22.62,22.63a8,8,0,0,0,11.32-11.32ZM128,184a8,8,0,0,0-8,8v32a8,8,0,0,0,16,0V192A8,8,0,0,0,128,184ZM77.09,167.6,54.46,190.22a8,8,0,0,0,11.32,11.32L88.4,178.91A8,8,0,0,0,77.09,167.6ZM72,128a8,8,0,0,0-8-8H32a8,8,0,0,0,0,16H64A8,8,0,0,0,72,128ZM65.78,54.46A8,8,0,0,0,54.46,65.78L77.09,88.4A8,8,0,0,0,88.4,77.09Z"></path>
        </svg>
    )
}

function SpinnerGap({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M136,32V64a8,8,0,0,1-16,0V32a8,8,0,0,1,16,0Zm88,88H192a8,8,0,0,0,0,16h32a8,8,0,0,0,0-16Zm-45.09,47.6a8,8,0,0,0-11.31,11.31l22.62,22.63a8,8,0,0,0,11.32-11.32ZM128,184a8,8,0,0,0-8,8v32a8,8,0,0,0,16,0V192A8,8,0,0,0,128,184ZM77.09,167.6,54.46,190.22a8,8,0,0,0,11.32,11.32L88.4,178.91A8,8,0,0,0,77.09,167.6ZM72,128a8,8,0,0,0-8-8H32a8,8,0,0,0,0,16H64A8,8,0,0,0,72,128ZM65.78,54.46A8,8,0,0,0,54.46,65.78L77.09,88.4A8,8,0,0,0,88.4,77.09Z"></path>
        </svg>
    )
}

function X({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
        </svg>
    )
}

function IconPlus({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
        </svg>
    )
}

function ClipboardText({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M168,152a8,8,0,0,1-8,8H96a8,8,0,0,1,0-16h64A8,8,0,0,1,168,152Zm-8-40H96a8,8,0,0,0,0,16h64a8,8,0,0,0,0-16Zm56-64V216a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V48A16,16,0,0,1,56,32H92.26a47.92,47.92,0,0,1,71.48,0H200A16,16,0,0,1,216,48ZM96,64h64a32,32,0,0,0-64,0ZM200,48H173.25A47.93,47.93,0,0,1,176,64v8a8,8,0,0,1-8,8H88a8,8,0,0,1-8-8V64a47.93,47.93,0,0,1,2.75-16H56V216H200Z"></path>
        </svg>
    )
}

function Chat({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-4 w-4 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M216,48H40A16,16,0,0,0,24,64V224a15.84,15.84,0,0,0,9.25,14.5A16.05,16.05,0,0,0,40,240a15.89,15.89,0,0,0,10.25-3.78.69.69,0,0,0,.13-.11L82.5,208H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48ZM40,224h0ZM216,192H82.5a16,16,0,0,0-10.3,3.75l-.12.11L40,224V64H216Z"></path>
        </svg>
    )
}

function BlendAILogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            id="Layer_2"
            data-name="Layer 2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            className={cn("h-10 w-10 fill-white", className)}
        >
            <rect
                x="120.1"
                y="9.9"
                width="13.5"
                height="240"
                transform="translate(-49.1 174.4) rotate(-59.9)"
                style={{strokeWidth: '50'}} 
            />
            <rect
                x="8.4"
                y="120.9"
                width="238.9"
                height="13.5"
                transform="translate(0 255.2) rotate(-89.9)"
            />
            <rect
                x="8"
                y="121.7"
                width="238.5"
                height="13.5"
                transform="translate(-47.1 80.5) rotate(-29.9)"
            />
            <path
                d="M240.5,75.3h0l-.2,106.1c0,7.4-4,14.2-10.4,17.9l-92,52.8c-6.4,3.7-14.3,3.6-20.7,0l-91.7-53.3c-6.4-3.7-10.3-10.6-10.3-17.9h0l.2-106.1c0-7.4,4-14.2,10.4-17.9L117.8,4.1c6.4-3.7,14.3-3.6,20.7,0h0l91.8,53.2c6.4,3.7,10.3,10.6,10.3,17.9h0ZM28.7,181c0,2.5,1.4,4.9,3.6,6.2l91.8,53.2c2.2,1.3,4.9,1.3,7.2,0l92-52.8c2.2-1.3,3.6-3.6,3.6-6.2l.2-106.1h0c0-2.6-1.4-4.9-3.6-6.2L131.7,15.9c-2.2-1.3-4.9-1.3-7.2,0l-92,52.8c-2.2,1.3-3.6,3.6-3.6,6.2l-.2,106.1h0Z"
            />
        </svg>
    )
}



function BlendAINewLogo({ 
    className, 
    fillColor = "#EA580C", 
    strokeAndCircleColor = "white", 
    ...props 
}: React.ComponentProps<"svg"> & { 
    fillColor?: string,
    strokeAndCircleColor?: string 
}) {
    return (
        <svg width="742" height="751" viewBox="0 0 742 751" fill="none" xmlns="http://www.w3.org/2000/svg" className={cn("h-10 w-10 ", className)}>
            <g clipPath="url(#clip0_10_75)">
                <rect width="742" height="751" fill={fillColor === "transparent" ? "transparent" : "#EA580C"} />
                <path d="M536.583 536L424 601" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M535.583 281L423 346" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M319.583 406L207 471" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M318.583 153L206 218" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M588 441V311" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M372 566V436" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M371 316V186" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M154 441V311" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M327.205 601L214.622 536" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M536.583 471L424 406" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M319.583 336L207 271" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <path d="M535.583 218L423 153" stroke={strokeAndCircleColor} strokeWidth="24" strokeLinecap="round" />
                <circle cx="371" cy="126" r="30" fill={strokeAndCircleColor} />
                <circle cx="588" cy="251" r="30" fill={strokeAndCircleColor} />
                <circle cx="588" cy="501" r="30" fill={strokeAndCircleColor} />
                <circle cx="371" cy="626" r="30" fill={strokeAndCircleColor} />
                <circle cx="154" cy="501" r="30" fill={strokeAndCircleColor} />
                <circle cx="154" cy="251" r="30" fill={strokeAndCircleColor} />
                <circle cx="371" cy="376" r="30" fill={strokeAndCircleColor} />
            </g>
            <defs>
                <clipPath id="clip0_10_75">
                    <rect width="742" height="751" fill="white" />
                </clipPath>
            </defs>
        </svg>
    )
}






function BlendAIWordLogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            id="Layer_1"
            data-name="Layer 1"
            className={cn("h-10 w-10 fill-foreground", className)}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 403.1 64.6"
        >
            <path d="M17.7,1.4c12.5,0,19.5,0,21.1-.1h2.9c1.1,0,1.6,0,1.7.2.2.3.3,4.6.3,13.1s-.1,12.9-.3,13.2c-.2.2-1.1.3-2.9.3s-2.7.1-2.9.3c-.2.2-.2,1.6-.2,4.2s0,3.9.2,4.2c.2.3,1.2.4,2.9.4s2.7.1,2.9.3c.2.3.3,4.6.3,13s-.1,12.8-.3,13.1c-.4.4-11.1.7-32,.7s-10.6,0-10.7-.2c-.3-.3-.4-10.7-.4-31.1S.5,2.1.7,1.8c.2-.2,5.9-.4,17-.4ZM37.2,10.6c-.2-.2-.8-.3-2-.3-1.3.1-6.2.2-14.8.2-8.7,0-13.2.2-13.4.4-.2.2-.3,2.7-.3,7.6s.1,9.2.4,9.5c.3.2,3.3.3,9,.3,13.6,0,20.5-.2,20.9-.7.3-.3.4-3.1.4-8.6s-.1-8.3-.3-8.6ZM37.2,37.4c-.2-.2-1.2-.3-3-.3-2.1.1-7.1.2-14.8.2-8,0-12.1.2-12.3.4-.2.3-.3,3.1-.3,8.6s.1,8.3.4,8.6c.2.2,3.2.3,9,.3,13.6,0,20.6-.2,21-.7.2-.2.3-3.1.3-8.6s-.1-8.3-.3-8.6Z" />
            <path d="M56.9,1.1c.2-.2,1.1-.3,2.8-.3s2.7.1,2.9.3c.2.3.3,9,.3,26.1s0,25.9.2,26.1c.2.3,6.2.4,18,.4s17.9.1,18.1.3c.2.2.2,1.6.2,4.2s0,3.9-.2,4.2c-.2.3-7.3.4-21.2.4s-20.9-.1-21.1-.4c-.2-.3-.3-10.5-.3-30.6s.1-30.4.3-30.7Z" />
            <path d="M112.4,1.5c.1-.2,7.2-.3,21.2-.3h12.6c5.6,0,8.5.1,8.7.4.3.4.5,1.8.5,4.2s-.1,3.8-.3,4.1c-.2.2-6.2.3-18.2.3s-18.2.1-18.3.3c-.2.3-.3,3.1-.3,8.6s0,8.3.3,8.6c.2.2,4.2.3,12.1.3s11.9.1,12.1.3c.2.3.3,1.6.3,4.1s0,3.7-.3,3.9c-.2.3-4.2.4-12.1.4s-11.9.1-12.1.3c-.2.2-.2,3.1-.2,8.7s.1,8.4.3,8.7c.3.2,2.7.3,7.5.3s5.9,0,11.1-.1c5.2,0,8.9-.1,11-.1,4.4,0,6.7.1,6.9.3.2.3.3,1.7.3,4.3s-.1,4-.3,4.3c-.2.3-8.4.4-24.5.4s-18.1-.1-18.2-.3c-.2-.3-.2-13.1-.2-38.4s0-23.3.2-23.6Z" />
            <path d="M170.3,1.7c1,0,1.9-.1,2.8-.1s.9,0,1,.2c.2.2.2,1.6.2,4.2s0,3.9.2,4.2c.2.2,1.1.3,2.9.3s2.7.1,2.9.4c.2.3.3,1.6.3,3.9s0,3.8.2,4.1c.2.2,1.1.3,2.8.3s2.5.1,2.7.4c.2.2.2,1.6.2,4.2s.1,3.8.3,4.1c.2.3,1.2.4,2.9.4s2.7.1,2.9.3c.2.2.2,1.6.2,4.1s0,3.7.2,3.9c.2.3,1.2.4,2.9.4s2.7.1,2.9.3c.2.3.3,1.7.3,4.2s0,3.9.2,4.2c.2.2,1,.3,2.6.3s2.5-.1,2.6-.3c.2-.3.3-7.6.3-21.9s0-21.8.2-22c.2-.3,1.2-.4,2.9-.4s2.7.1,2.9.4c.2.3.3,10.6.3,30.9s-.1,30.5-.3,30.8c-.2.3-1.2.4-2.9.4s-2.7-.1-2.9-.4c-.2-.2-.2-1.5-.2-3.9s-.1-3.8-.3-4.1c-.2-.2-1.1-.3-2.7-.3s-2.6-.1-2.8-.4c-.2-.2-.2-1.6-.2-4.1s-.1-3.9-.3-4.2c-.2-.3-1.1-.4-2.7-.4s-2.6-.1-2.8-.3c-.2-.2-.2-1.6-.2-4.1s0-3.7-.2-3.9c-.2-.3-1.2-.4-2.9-.4s-2.7-.1-2.9-.3c-.2-.3-.3-1.7-.3-4.2s0-3.9-.2-4.2c-.2-.2-1.1-.3-2.7-.3s-2.6-.1-2.8-.4c-.2-.2-.2-1.5-.2-3.9s-.1-3.8-.3-4.1c-.2-.3-1.2-.4-2.9-.4s-2.7.1-2.9.4c-.2.2-.2,7.5-.2,21.8s0,21.6-.2,21.8c-.2.3-1.1.4-2.8.4s-2.5-.1-2.7-.4c-.2-.3-.3-10.5-.3-30.7s.1-30.4.3-30.7c0-.1.7-.3,1.7-.4Z" />
            <path d="M223.7,31.6c0-20.2.1-30.4.3-30.7.2-.3,6.2-.4,18.1-.4s17.9.1,18.1.4c.2.2.2,1.6.2,4.2s0,3.8.3,4.1c.2.3,1.2.4,2.9.4s2.7.1,2.9.3c.2.3.3,7.6.3,21.8s-.1,21.5-.3,21.8c-.2.2-1.1.3-2.9.3s-2.7.1-2.9.4c-.2.3-.3,1.6-.3,3.9s0,3.8-.2,4.1c-.2.3-6.2.4-18.1.4s-17.9-.1-18.1-.4c-.2-.3-.3-10.5-.3-30.7ZM260.4,31.6c0-14.3,0-21.5-.2-21.7s-4.4-.3-12.8-.3-17,.1-17.3.4c-.3.4-.4,3-.4,7.9s0,2.7,0,5.8c0,3,0,5.8,0,8.2,0,14.3.2,21.6.2,21.7.1.2,5.1.3,14.9.3s14.9-.1,15.1-.4c.2-.3.3-7.6.3-21.8Z" />
            <path d="M303.7,32.1c0-20.5.1-31,.3-31.3.2-.3,7.3-.4,21.4-.4s21.1.1,21.3.4c.2.3.3,10.7.3,31.3s-.1,31-.3,31.3c-.2.3-1.2.4-2.9.4s-2.7-.1-2.9-.4c-.2-.2-.2-6.1-.2-17.7s-.1-17.4-.3-17.7c-.2-.3-5.2-.4-15.1-.4s-14.9.1-15.1.4c-.2.2-.2,6.1-.2,17.7s-.1,17.4-.3,17.7c-.2.3-1.1.4-2.8.4s-2.7-.1-2.9-.4c-.2-.3-.3-10.7-.3-31.3ZM340.4,9.9c-.2-.3-5.2-.4-15.1-.4s-14.9.1-15.1.4c-.2.3-.3,1.7-.3,4.2s.1,3.8.3,4.1c.2.3,5.2.4,15.1.4s14.9-.1,15.1-.4c.2-.3.3-1.6.3-4.1s-.1-3.9-.3-4.2Z" />
            <path d="M359.7,6c0-2.5.1-3.9.3-4.2.2-.3,7.3-.4,21.2-.4s21,.1,21.2.4c.2.2.2,1.6.2,4.2s0,3.9-.2,4.2-3.1.3-8.9.3-8.7.1-8.9.4c-.2.3-.3,7.6-.3,21.8s.1,21.5.3,21.8c.2.2,3.1.3,8.9.3s8.7.1,8.9.3c.2.3.3,1.7.3,4.2s-.1,3.9-.3,4.2c-.2.3-7.3.4-21.2.4s-21-.1-21.2-.4c-.2-.3-.3-1.7-.3-4.2s.1-3.9.3-4.2c.2-.2,3.1-.3,9-.3s8.8-.1,9-.3c.2-.3.3-7.6.3-21.8s-.1-21.5-.3-21.8c-.2-.3-3.2-.4-9-.4s-8.8-.1-9-.3c-.2-.3-.3-1.7-.3-4.2Z" />
        </svg>
    )
}

function PaintBrush({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M232,32a8,8,0,0,0-8-8c-44.08,0-89.31,49.71-114.43,82.63A60,60,0,0,0,32,164c0,30.88-19.54,44.73-20.47,45.37A8,8,0,0,0,16,224H92a60,60,0,0,0,57.37-77.57C182.3,121.31,232,76.08,232,32ZM92,208H34.63C41.38,198.41,48,183.92,48,164a44,44,0,1,1,44,44Zm32.42-94.45q5.14-6.66,10.09-12.55A76.23,76.23,0,0,1,155,121.49q-5.9,4.94-12.55,10.09A60.54,60.54,0,0,0,124.42,113.55Zm42.7-2.68a92.57,92.57,0,0,0-22-22c31.78-34.53,55.75-45,69.9-47.91C212.17,55.12,201.65,79.09,167.12,110.87Z"></path>
        </svg>
    )
}

function VideoCamera({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M251.77,73a8,8,0,0,0-8.21.39L208,97.05V72a16,16,0,0,0-16-16H32A16,16,0,0,0,16,72V184a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V159l35.56,23.71A8,8,0,0,0,248,184a8,8,0,0,0,8-8V80A8,8,0,0,0,251.77,73ZM192,184H32V72H192V184Zm48-22.95-32-21.33V116.28L240,95Z"></path>
        </svg>
    )
}

function CloudArrowDown({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M248,128a87.34,87.34,0,0,1-17.6,52.81,8,8,0,1,1-12.8-9.62A71.34,71.34,0,0,0,232,128a72,72,0,0,0-144,0,8,8,0,0,1-16,0,88,88,0,0,1,3.29-23.88C74.2,104,73.1,104,72,104a48,48,0,0,0,0,96H96a8,8,0,0,1,0,16H72A64,64,0,1,1,81.29,88.68,88,88,0,0,1,248,128Zm-69.66,42.34L160,188.69V128a8,8,0,0,0-16,0v60.69l-18.34-18.35a8,8,0,0,0-11.32,11.32l32,32a8,8,0,0,0,11.32,0l32-32a8,8,0,0,0-11.32-11.32Z"></path>
        </svg>
    )
}


function MistralAI({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 233"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid"
        >
            <title>Mistral AI</title>
            <g>
                <rect
                    fill="#000000"
                    x="186.181818"
                    y="0"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#F7D046"
                    x="209.454545"
                    y="0"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="0"
                    y="0"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="0"
                    y="46.5454545"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="0"
                    y="93.0909091"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="0"
                    y="139.636364"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="0"
                    y="186.181818"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#F7D046"
                    x="23.2727273"
                    y="0"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#F2A73B"
                    x="209.454545"
                    y="46.5454545"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#F2A73B"
                    x="23.2727273"
                    y="46.5454545"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="139.636364"
                    y="46.5454545"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#F2A73B"
                    x="162.909091"
                    y="46.5454545"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#F2A73B"
                    x="69.8181818"
                    y="46.5454545"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EE792F"
                    x="116.363636"
                    y="93.0909091"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EE792F"
                    x="162.909091"
                    y="93.0909091"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EE792F"
                    x="69.8181818"
                    y="93.0909091"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="93.0909091"
                    y="139.636364"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EB5829"
                    x="116.363636"
                    y="139.636364"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EE792F"
                    x="209.454545"
                    y="93.0909091"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EE792F"
                    x="23.2727273"
                    y="93.0909091"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="186.181818"
                    y="139.636364"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EB5829"
                    x="209.454545"
                    y="139.636364"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#000000"
                    x="186.181818"
                    y="186.181818"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EB5829"
                    x="23.2727273"
                    y="139.636364"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EA3326"
                    x="209.454545"
                    y="186.181818"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
                <rect
                    fill="#EA3326"
                    x="23.2727273"
                    y="186.181818"
                    width="46.5454545"
                    height="46.5454545"
                ></rect>
            </g>
        </svg>
    )
}


function Meta({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="56.329999999999984 457.15000000000003 2387.3400000000006 1585.74"
            className={cn("h-4 w-4 fill-foreground", className)}
        >
            <linearGradient
                id="a"
                gradientTransform="matrix(1 0 0 -1 0 2500)"
                gradientUnits="userSpaceOnUse"
                x1="573.85"
                x2="2217.65"
                y1="1291.09"
                y2="1208.07"
            >
                <stop offset="0" stopColor="#0064e1" />
                <stop offset=".4" stopColor="#0064e1" />
                <stop offset=".83" stopColor="#0073ee" />
                <stop offset="1" stopColor="#0082fb" />
            </linearGradient>
            <linearGradient
                id="b"
                gradientTransform="matrix(1 0 0 -1 0 2500)"
                gradientUnits="userSpaceOnUse"
                x1="400.24"
                x2="400.24"
                y1="888.86"
                y2="1494.91"
            >
                <stop offset="0" stopColor="#0082fb" />
                <stop offset="1" stopColor="#0064e0" />
            </linearGradient>
            <path
                d="M314.19 1502.88c0 91.16 20 161.14 46.16 203.48 34.29 55.46 85.43 79 137.57 79 67.24 0 128.76-16.69 247.31-180.66 95-131.42 206.89-315.89 282.19-431.54l127.52-195.93c88.58-136.07 191.11-287.33 308.67-389.86 96-83.69 199.5-130.18 303.69-130.18 174.93 0 341.55 101.37 469.07 291.49 139.56 208.21 207.3 470.48 207.3 741.12 0 160.9-31.71 279.12-85.68 372.52-52.13 90.32-153.75 180.57-324.69 180.57v-257.57c146.37 0 182.89-134.5 182.89-288.42 0-219.34-51.14-462.75-163.8-636.68-79.94-123.37-183.55-198.75-297.54-198.75-123.29 0-222.5 93-334 258.77-59.28 88.09-120.13 195.43-188.46 316.56l-75.22 133.25C1006.09 1638 967.81 1699 892.26 1799.68 759.85 1976 646.77 2042.85 497.92 2042.85c-176.59 0-288.25-76.47-357.41-191.7-56.45-93.89-84.18-217.1-84.18-357.48z"
                fill="#0081fb"
            />
            <path
                d="M259.65 766.82c118.22-182.23 288.83-309.67 484.51-309.67 113.32 0 226 33.54 343.62 129.6 128.68 105 265.83 278 436.94 563l61.35 102.25c148.11 246.74 232.37 373.68 281.69 433.54 63.42 76.87 107.84 99.79 165.54 99.79 146.37 0 182.89-134.5 182.89-288.42l227.48-7.14c0 160.9-31.71 279.12-85.68 372.52-52.13 90.32-153.75 180.57-324.69 180.57-106.27 0-200.41-23.08-304.52-121.3-80-75.38-173.6-209.29-245.58-329.67l-214.11-357.65c-107.42-179.49-206-313.32-263-373.93C944.73 795.13 865.86 716.43 740 716.43c-101.86 0-188.37 71.48-260.76 180.82z"
                fill="url(#a)"
            />
            <path
                d="M740 716.43c-101.86 0-188.37 71.48-260.76 180.82-102.37 154.5-165 384.63-165 605.63 0 91.16 20 161.14 46.16 203.48l-219.89 144.79c-56.45-93.89-84.18-217.1-84.18-357.48 0-255.29 70.07-521.37 203.32-726.85 118.22-182.23 288.83-309.67 484.51-309.67z"
                fill="url(#b)"
            />
        </svg>
    )
}


function Perplexity({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg"             className={cn("h-4 w-4", className)}        viewBox="-4 -9.53674e-7 36 36">
            <path 
                d="m23.566,1.398l-9.495,9.504h9.495V1.398v2.602V1.398Zm-9.496,9.504L4.574,1.398v9.504h9.496Zm-.021-10.902v36m9.517-15.596l-9.495-9.504v13.625l9.495,9.504v-13.625Zm-18.991,0l9.496-9.504v13.625l-9.496,9.504v-13.625ZM.5,10.9v13.57h4.074v-4.066l9.496-9.504H.5Zm13.57,0l9.495,9.504v4.066h4.075v-13.57h-13.57Z" 
                stroke="#1f98a8" 
                strokeWidth="2" 
                fill="none" 
            />
        </svg>
    )
}




function StabilityAILogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            className={cn("h-4 w-4", className)}
            viewBox="0 0 256 213"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid"
        >
            <path
                className="fill-foreground"
                d="M72.418311,212.449785 C121.895828,212.449785 154.076327,186.244638 154.076327,146.823527 C154.076327,116.252053 134.503517,96.8259255 99.5072246,88.7808007 L77.0381637,82.0413304 C57.327608,77.6165118 45.8227858,72.3031898 48.5334508,58.7294108 C50.7884827,47.4372353 57.5348302,41.0624336 73.2228235,41.0624336 C123.095165,41.0624336 141.57308,58.7294108 141.57308,58.7294108 L141.57308,16.2373709 C141.57308,16.2373709 123.58314,0 73.2228235,0 C25.7565874,0 2.1513352e-14,24.4237956 2.1513352e-14,62.235882 C2.1513352e-14,92.8073562 17.8493354,110.58622 54.0523968,119.033601 C56.5861391,119.667138 57.8811457,119.992613 57.9374165,120.010028 C63.4441876,121.714275 70.87496,123.966007 80.2297338,126.765222 C98.7335207,131.190041 103.491968,135.886327 103.491968,149.965296 C103.491968,162.837495 90.1175855,170.154389 72.418311,170.154389 C21.4319569,170.154389 2.1513352e-14,144.361226 2.1513352e-14,144.361226 L2.1513352e-14,191.438959 C2.1513352e-14,191.438959 13.4024656,212.449785 72.418311,212.449785 Z"
            ></path>
            <path
                className="fill-[#E80000]"
                d="M225.442113,209.266419 C242.957,209.266419 256,196.596076 256,179.453847 C256,161.938961 243.329657,149.641275 225.442113,149.641275 C207.927227,149.641275 195.256884,161.938961 195.256884,179.453847 C195.256884,196.968733 207.927227,209.266419 225.442113,209.266419 Z"
            ></path>
        </svg>
    )
}

function IconNotches({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M213.66,133.66l-80,80a8,8,0,0,1-11.32-11.32l80-80a8,8,0,0,1,11.32,11.32Zm-16-99.32a8,8,0,0,0-11.32,0l-152,152a8,8,0,0,0,11.32,11.32l152-152A8,8,0,0,0,197.66,34.34Z"></path>
        </svg>
    )
}

function DiscordLogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M104,140a12,12,0,1,1-12-12A12,12,0,0,1,104,140Zm60-12a12,12,0,1,0,12,12A12,12,0,0,0,164,128Zm74.45,64.9-67,29.71a16.17,16.17,0,0,1-21.71-9.1l-8.11-22q-6.72.45-13.63.46t-13.63-.46l-8.11,22a16.18,16.18,0,0,1-21.71,9.1l-67-29.71a15.93,15.93,0,0,1-9.06-18.51L38,58A16.07,16.07,0,0,1,51,46.14l36.06-5.93a16.22,16.22,0,0,1,18.26,11.88l3.26,12.84Q118.11,64,128,64t19.4.93l3.26-12.84a16.21,16.21,0,0,1,18.26-11.88L205,46.14A16.07,16.07,0,0,1,218,58l29.53,116.38A15.93,15.93,0,0,1,238.45,192.9ZM232,178.28,202.47,62s0,0-.08,0L166.33,56a.17.17,0,0,0-.17,0l-2.83,11.14c5,.94,10,2.06,14.83,3.42A8,8,0,0,1,176,86.31a8.09,8.09,0,0,1-2.16-.3A172.25,172.25,0,0,0,128,80a172.25,172.25,0,0,0-45.84,6,8,8,0,1,1-4.32-15.4c4.82-1.36,9.78-2.48,14.82-3.42L89.83,56s0,0-.12,0h0L53.61,61.93a.17.17,0,0,0-.09,0L24,178.33,91,208a.23.23,0,0,0,.22,0L98,189.72a173.2,173.2,0,0,1-20.14-4.32A8,8,0,0,1,82.16,170,171.85,171.85,0,0,0,128,176a171.85,171.85,0,0,0,45.84-6,8,8,0,0,1,4.32,15.41A173.2,173.2,0,0,1,158,189.72L164.75,208a.22.22,0,0,0,.21,0Z"></path>
        </svg>
    )
}

function AnthropicLogo({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            width="46"
            height="32"
            viewBox="0 0 46 32"
            className={cn("h-10 w-10 fill-foreground", className)}
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M32.73 0h-6.945L38.45 32h6.945L32.73 0ZM12.665 0 0 32h7.082l2.59-6.72h13.25l2.59 6.72h7.082L19.929 0h-7.264Zm-.702 19.337 4.334-11.246 4.334 11.246h-8.668Z"></path>
        </svg>
    )
}

function IconGraph({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M200,152a31.84,31.84,0,0,0-19.53,6.68l-23.11-18A31.65,31.65,0,0,0,160,128c0-.74,0-1.48-.08-2.21l13.23-4.41A32,32,0,1,0,168,104c0,.74,0,1.48.08,2.21l-13.23,4.41A32,32,0,0,0,128,96a32.59,32.59,0,0,0-5.27.44L115.89,81A32,32,0,1,0,96,88a32.59,32.59,0,0,0,5.27-.44l6.84,15.4a31.92,31.92,0,0,0-8.57,39.64L73.83,165.44a32.06,32.06,0,1,0,10.63,12l25.71-22.84a31.91,31.91,0,0,0,37.36-1.24l23.11,18A31.65,31.65,0,0,0,168,184a32,32,0,1,0,32-32Zm0-64a16,16,0,1,1-16,16A16,16,0,0,1,200,88ZM80,56A16,16,0,1,1,96,72,16,16,0,0,1,80,56ZM56,208a16,16,0,1,1,16-16A16,16,0,0,1,56,208Zm56-80a16,16,0,1,1,16,16A16,16,0,0,1,112,128Zm88,72a16,16,0,1,1,16-16A16,16,0,0,1,200,200Z"></path>
        </svg>
    )
}

function IconChatText({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M216,48H40A16,16,0,0,0,24,64V224a15.85,15.85,0,0,0,9.24,14.5A16.13,16.13,0,0,0,40,240a15.89,15.89,0,0,0,10.25-3.78.69.69,0,0,0,.13-.11L82.5,208H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48ZM40,224h0ZM216,192H82.5a16,16,0,0,0-10.3,3.75l-.12.11L40,224V64H216ZM88,112a8,8,0,0,1,8-8h64a8,8,0,0,1,0,16H96A8,8,0,0,1,88,112Zm0,32a8,8,0,0,1,8-8h64a8,8,0,1,1,0,16H96A8,8,0,0,1,88,144Z"></path>
        </svg>
    )
}

function IconDotsThreeOutline({
    className,
    ...props
}: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M128,96a32,32,0,1,0,32,32A32,32,0,0,0,128,96Zm0,48a16,16,0,1,1,16-16A16,16,0,0,1,128,144ZM48,96a32,32,0,1,0,32,32A32,32,0,0,0,48,96Zm0,48a16,16,0,1,1,16-16A16,16,0,0,1,48,144ZM208,96a32,32,0,1,0,32,32A32,32,0,0,0,208,96Zm0,48a16,16,0,1,1,16-16A16,16,0,0,1,208,144Z"></path>
        </svg>
    )
}

function Trash({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            className={cn("h-10 w-10 fill-foreground", className)}
            viewBox="0 0 256 256"
        >
            <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
        </svg>
    )
}



function AiSparks({ className, ...props }: React.ComponentProps<"svg">) {
    return (
        <svg className={cn("h-10 w-10", className)} viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" {...props}>
            <linearGradient id="SVGID_1_" gradientUnits="userSpaceOnUse" x1="37.14" x2="88.666" y1="46.541" y2="76.29" gradientTransform="matrix(1.871535, 0.657907, -0.657909, 1.871531, -14.052436, -103.563389)">
                <stop offset="0" stopColor="#00a5e9" />
                <stop offset="1" stopColor="#d83fff" />
            </linearGradient>
            <g transform="matrix(1, 0, 0, 1, 2.842170943040401e-14, 0)">
                <g fill="url(#SVGID_1_)">
                    <path d="M 104.317 42.94 C 102.014 50.129 96.324 55.819 89.135 58.122 C 96.324 60.426 102.014 66.116 104.317 73.305 C 106.621 66.116 112.309 60.426 119.497 58.122 C 112.309 55.819 106.621 50.13 104.317 42.94 Z" />
                    <path d="M 48.528 37.918 C 44.756 58.911 28.162 75.507 7.172 79.281 C 28.162 83.053 44.756 99.651 48.528 120.642 C 52.301 99.649 68.895 83.053 89.885 79.281 C 68.895 75.507 52.301 58.911 48.528 37.918 Z" />
                    <path d="M 68.021 1.77 C 65.66 11.628 57.884 19.404 48.027 21.766 C 57.884 24.127 65.661 31.904 68.021 41.763 C 70.383 31.904 78.158 24.127 88.016 21.766 C 78.158 19.404 70.383 11.628 68.021 1.77 Z" />
                </g>
            </g>
        </svg>
    )
}



export {
    GithubLogo,
    OllamaLogo,
    GoogleLogo,
    Moon,
    Sun,
    ArrowCircleRight,
    ProviderIcon,
    IconUser,
    IconResize,
    IconGear,
    PlusCircle,
    MinusCircle,
    SignOut,
    User,
    Pencil,
    Check,
    CaretDown,
    Circle,
    ArrowLeft,
    ArrowRight,
    Microphone,
    Stop,
    Images,
    ImagesSquare,
    IconSpinner,
    SpinnerGap,
    X,
    IconPlus,
    ClipboardText,
    Chat,
    BlendAILogo,
    BlendAIWordLogo,
    PaintBrush,
    VideoCamera,
    CloudArrowDown,
    Replicate,
    MistralAI,
    OpenAI,
    Perplexity,
    StabilityAILogo,
    IconNotches,
    DiscordLogo,
    AnthropicLogo,
    IconGraph,
    IconChatText,
    IconDotsThreeOutline,
    Trash,
    AiSparks,
    BlendAINewLogo,
}
